import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { openInAppBrowser, runningUnderNativePlatform } from 'src/store/running-under-native-platform';
import type { ActionInput } from './action-input';

/**
 * En kommando link eller "knapp".
 * STATUS OK
 */
@customElement('d-action')
export class DAction extends LitElement {
  static readonly styles = css`
    :host {
      display: inline-block;
      color: var(--themeColor);
      mix-blend-mode: multiply;
      font-size: 11px;
      font-family: var(--small), sans-serif;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    :host([theme-page]) {
      color: var(--themeColorDarkerOne);
      mix-blend-mode: normal;
    }

    :host([gray]),
    :host([gray]) a,
    :host([gray]) span {
      color: var(--linkColorGray);
    }

    :host([outskirts]) {
      color: white;
      font-weight: 500;
      mix-blend-mode: normal;
    }

    :host([outskirts][plain]) {
      font-weight: 200;
    }

    span,
    a {
      display: inline-block;
      padding: 6px 8px;
      cursor: pointer;
      text-decoration: none;
      color: inherit;
      outline: none;
    }

    :host([small]) span,
    :host([small]) a,
    span[small],
    a[small] {
      font-size: 10px;
    }

    :host([plain]),
    span[plain],
    a[plain] {
      font-family: var(--mainSans), sans-serif;
      font-weight: 400;
      font-size: 15px;
      text-transform: none;
      letter-spacing: normal;
    }

    :host([mini]),
    span[mini],
    a[mini] {
      font-family: var(--mainSans), sans-serif;
      font-weight: 400;
      font-size: 13px;
      text-transform: none;
      letter-spacing: normal;
    }

    :host([mini]) span,
    :host([mini]) a {
      padding: 4px 8px;
    }

    :host([disabled]) a,
    :host([disabled]) span,
    :host a[disabled],
    :host span[disabled] {
      color: inherit;
      opacity: 0.5;
      cursor: default;
    }

    :host([button]) a,
    :host([button]) span {
      border-radius: 4px;
      background: var(--themeColor);
      padding: 10px 14px;
      font-family: var(--mainSans), sans-serif;
      font-weight: normal;
      font-size: 15px;
      line-height: normal;
      text-transform: none;
      letter-spacing: normal;
      color: white;
      cursor: pointer;
    }

    :host([button][green]) a,
    :host([button][green]) span {
      background: hsl(96, 98%, 36%);
      color: white;
    }

    @media (hover: hover) {
      a:hover,
      span:hover {
        color: black;
      }

      :host([gray]) a:hover,
      :host([gray]) span:hover {
        color: var(--themeColorDarkerOne);
      }

      :host([dark]) a:hover,
      :host([dark]) span:hover {
        color: white;
      }

      :host([delete]) a:hover,
      :host([delete]) span:hover {
        color: red;
      }

      :host([disabled]) a:hover,
      :host([disabled]) span:hover,
      :host a[disabled]:hover,
      :host span[disabled]:hover {
        color: inherit;
        opacity: 0.5;
        cursor: default;
      }

      :host([button]) a:hover,
      :host([button]) span:hover {
        background: var(--themeColorDarkerOne);
        color: white;
      }

      :host([button][dark]) a:hover,
      :host([button][dark]) span:hover {
        background: var(--themeColor);
        color: white;
      }

      :host([button][green]) a:hover,
      :host([button][green]) span:hover {
        background: hsl(96, 100%, 29%);
        color: white;
      }

      :host([button][delete]) a:hover,
      :host([button][delete]) span:hover {
        background: red;
        color: white;
      }

      :host([button][disabled]) a:hover,
      :host([button][disabled]) span:hover {
        background: var(--themeColor);
        color: white;
      }
    }
  `;

  @property({ type: Boolean, attribute: true, reflect: true })
  gray = false;
  @property({ type: Boolean, attribute: true, reflect: true })
  small = false;
  @property({ type: Boolean, attribute: true, reflect: true })
  mini = false;
  @property({ type: Boolean, attribute: true, reflect: true })
  plain = false;
  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;
  @property({ type: String })
  href = '';
  @property({ type: Boolean })
  blank = false;
  @property({ type: Boolean, attribute: true, reflect: true })
  outskirts = false;
  @property({ type: Object })
  input: ActionInput | undefined;

  render() {
    if (this.input) {
      if (runningUnderNativePlatform() && this.input.href && this.input.target === '_blank') {
        const href = this.input.href;
        let disabled = false;
        if (this.input.disabled) {
          disabled = true;
        }
        return html`
          <span
            ?disabled="${disabled}"
            @click=${async () => {
              if (!disabled) {
                await openInAppBrowser(href);
              }
            }}
            slot="${this.input.slot ? this.input.slot : ''}"
          >
            ${this.input.name}
          </span>
        `;
      } else if (this.input.href) {
        return html`
          <a
            href="${this.input.href}"
            slot="${this.input.slot ? this.input.slot : ''}"
            target="${ifDefined(this.blank ? '_blank' : undefined)}"
            ?small="${this.input.small}"
            ?mini="${this.input.mini}"
            ?plain="${this.input.plain}"
            ?disabled="${this.input.disabled}"
          >
            ${this.input.name}
          </a>
        `;
      }
      if (this.input.action) {
        let action = '';
        if (this.input.action) {
          action = this.input.action;
        }
        let disabled = false;
        if (this.input.disabled) {
          disabled = true;
        }
        return html`
          <span
            ?small="${this.input.small}"
            ?mini="${this.input.mini}"
            ?plain="${this.input.plain}"
            ?disabled="${disabled}"
            @click=${() => this.dispatch(disabled, action)}
            slot="${this.input.slot ? this.input.slot : ''}"
          >
            ${this.input.name}
          </span>
        `;
      }
    }

    if (runningUnderNativePlatform() && this.href && this.blank) {
      let disabled = false;
      if (this.disabled) {
        disabled = true;
      }
      return html`
        <span
          ?disabled="${disabled}"
          @click=${async () => {
            if (!disabled) {
              await openInAppBrowser(this.href);
            }
          }}
        >
          <slot></slot>
        </span>
      `;
    }

    return html`
      ${this.href === ''
        ? html`
            <span
              @click=${(e) => {
                e.preventDefault();
                e.stopPropagation();
                return this.dispatchEvent(
                  new CustomEvent('click', {
                    composed: true,
                    bubbles: true,
                  }),
                );
              }}
              ><slot></slot
            ></span>
          `
        : html`
            <a href="${this.href}" target="${ifDefined(this.blank ? '_blank' : undefined)}">
              <slot></slot>
            </a>
          `}
    `;
  }

  private dispatch(disabled, action) {
    if (!disabled && action) {
      this.dispatchEvent(new CustomEvent('action', { bubbles: true, composed: true, detail: action }));
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-action': DAction;
  }
}
